// @ts-nocheck
import { getLogger } from '@jitsi/logger';
import { FixedSizeArray } from 'lib-jitsi-meet/dist/esm/modules/qualitycontrol/QualityController';
import { DEFAULT_LAST_N, LAST_N_UNLIMITED, VIDEO_CODECS_BY_COMPLEXITY, VIDEO_QUALITY_LEVELS } from 'lib-jitsi-meet/dist/esm/service/RTC/StandardVideoQualitySettings';
import { QUALITY_EVENTS_WILDIX } from '../../../../components/quality-control/constant';
const logger = getLogger('CustomQualityController');
// Period for which the client will wait for the cpu limitation flag to be reset in the peerconnection stats before it
// attempts to rectify the situation by attempting a codec switch.
const LIMITED_BY_CPU_TIMEOUT = 60000;
// The min. value that lastN will be set to while trying to fix video qaulity issues.
const MIN_LAST_N = 3;
const QualityLimitationReason = {
    BANDWIDTH: 'bandwidth',
    CPU: 'cpu',
    NONE: 'none'
};
const QualityContoller = {
    applyOverride(qualityController) {
        const QualityContollerPrototype = Object.getPrototypeOf(qualityController);
        /**
         * Adjusts the lastN value so that fewer remote video sources are received from the bridge in an attempt to improve
         * encode resolution of the outbound video streams based on cpuLimited parameter passed. If cpuLimited is false,
         * the lastN value will slowly be ramped back up to the channelLastN value set in config.js.
         *
         * @param {boolean} cpuLimited - whether the endpoint is cpu limited or not.
         * @returns boolean - Returns true if an action was taken, false otherwise.
         */
        QualityContollerPrototype._lowerOrRaiseLastN = function (cpuLimited) {
            const lastN = this.receiveVideoController.getLastN();
            let newLastN = lastN;
            if (cpuLimited && (lastN !== LAST_N_UNLIMITED && lastN <= MIN_LAST_N)) {
                return false;
            }
            // If channelLastN is not set or set to -1 in config.js, the client will ramp up lastN to only up to 25.
            let { channelLastN = DEFAULT_LAST_N } = this._conference.options.config;
            channelLastN = channelLastN === LAST_N_UNLIMITED ? DEFAULT_LAST_N : channelLastN;
            if (cpuLimited) {
                const videoStreamsReceived = this._conference.getForwardedSources().length;
                newLastN = Math.floor(videoStreamsReceived / 2);
                if (newLastN < MIN_LAST_N) {
                    newLastN = MIN_LAST_N;
                }
                // Increment lastN by 1 every LAST_N_RAMPUP_TIME (60) secs.
            }
            else if (lastN < channelLastN) {
                newLastN++;
            }
            if (newLastN === lastN) {
                return false;
            }
            const isStillLimitedByCpu = newLastN < channelLastN;
            this.receiveVideoController.setLastNLimitedByCpu(isStillLimitedByCpu);
            logger.info(`QualityController - setting lastN=${newLastN}, limitedByCpu=${isStillLimitedByCpu}`);
            this.receiveVideoController.setLastN(newLastN);
            this._conference.eventEmitter.emit(QUALITY_EVENTS_WILDIX.LAST_N, newLastN);
            return true;
        };
        /**
         * Adjusts the requested resolution for remote video sources by updating the receiver constraints in an attempt to
         * improve the encode resolution of the outbound video streams.
         * @return {void}
         */
        QualityContollerPrototype._maybeLowerReceiveResolution = function () {
            const currentConstraints = this.receiveVideoController.getCurrentReceiverConstraints();
            const individualConstraints = currentConstraints.constraints;
            let maxHeight = 0;
            if (individualConstraints && Object.keys(individualConstraints).length) {
                for (const value of Object.values(individualConstraints)) {
                    const v = value;
                    maxHeight = Math.max(maxHeight, v.maxHeight);
                }
            }
            const currentLevel = VIDEO_QUALITY_LEVELS.findIndex(lvl => lvl.height <= maxHeight);
            // Do not lower the resolution to less than 180p.
            if (VIDEO_QUALITY_LEVELS[currentLevel].height === 180) {
                return;
            }
            this.receiveVideoController.setPreferredReceiveMaxFrameHeight(VIDEO_QUALITY_LEVELS[currentLevel + 1].height);
            this._conference.eventEmitter.emit(QUALITY_EVENTS_WILDIX.VIDEO_RESOLUTION, VIDEO_QUALITY_LEVELS[currentLevel + 1].height);
        };
        /**
         * Updates the codec preference order for the local endpoint on the active media session and switches the video
         * codec if needed.
         *
         * @param {number} trackId - The track ID of the local video track for which stats have been captured.
         * @returns {boolean} - Returns true if video codec was changed.
         */
        QualityContollerPrototype._maybeSwitchVideoCodec = function (trackId) {
            const stats = this._encodeTimeStats.get(trackId);
            const { codec, encodeResolution, localTrack } = stats.get(stats.size() - 1);
            const codecsByVideoType = VIDEO_CODECS_BY_COMPLEXITY[localTrack.getVideoType()];
            const codecIndex = codecsByVideoType.findIndex(val => val === codec.toLowerCase());
            // Do nothing if the encoder is using the lowest complexity codec already.
            if (codecIndex === codecsByVideoType.length - 1) {
                return false;
            }
            if (!this._limitedByCpuTimeout) {
                this._limitedByCpuTimeout = window.setTimeout(() => {
                    this._limitedByCpuTimeout = undefined;
                    const updatedStats = this._encodeTimeStats.get(trackId);
                    const latestSourceStats = updatedStats.get(updatedStats.size() - 1);
                    // If the encoder is still limited by CPU, switch to a lower complexity codec.
                    if (latestSourceStats.qualityLimitationReason === QualityLimitationReason.CPU
                        || encodeResolution < Math.min(localTrack.maxEnabledResolution, localTrack.getCaptureResolution())) {
                        // Send new video codec instead of current
                        this._conference.eventEmitter.emit(QUALITY_EVENTS_WILDIX.VIDEO_CODEC, codecsByVideoType[codecIndex + 1]);
                        return this.codecController.changeCodecPreferenceOrder(localTrack, codec);
                    }
                }, LIMITED_BY_CPU_TIMEOUT);
            }
            return false;
        };
        /**
         * Processes the outbound RTP stream stats as reported by the WebRTC peerconnection and makes runtime adjustments
         * to the client for better quality experience if the adaptive mode is enabled.
         *
         * @param {TraceablePeerConnection} tpc - The underlying WebRTC peerconnection where stats have been captured.
         * @param {Map<number, IOutboundRtpStats>} stats - Outbound-rtp stream stats per SSRC.
         * @returns void
         */
        // eslint-disable-next-line max-len
        QualityContollerPrototype._processOutboundRtpStats = function (tpc, stats) {
            if (!this._enableAdaptiveMode) {
                return;
            }
            const activeSession = this._conference.getActiveMediaSession();
            // Process stats only for the active media session.
            if (activeSession.peerconnection !== tpc) {
                return;
            }
            const statsPerTrack = new Map();
            for (const ssrc of stats.keys()) {
                const { codec, encodeTime, qualityLimitationReason, resolution, timestamp } = stats.get(ssrc);
                const track = tpc.getTrackBySSRC(ssrc);
                const trackId = track.rtcId;
                let existingStats = statsPerTrack.get(trackId);
                const encodeResolution = Math.min(resolution?.height, resolution?.width);
                const ssrcStats = {
                    encodeResolution,
                    encodeTime,
                    qualityLimitationReason
                };
                if (existingStats) {
                    existingStats.codec = codec;
                    existingStats.timestamp = timestamp;
                    existingStats.trackStats.push(ssrcStats);
                }
                else {
                    existingStats = {
                        codec,
                        timestamp,
                        trackStats: [ssrcStats]
                    };
                    statsPerTrack.set(trackId, existingStats);
                }
            }
            // Aggregate the stats for multiple simulcast streams with different SSRCs but for the same video stream.
            for (const trackId of statsPerTrack.keys()) {
                const { codec, timestamp, trackStats } = statsPerTrack.get(trackId);
                const totalEncodeTime = trackStats
                    .map((stat) => stat.encodeTime)
                    .reduce((totalValue, currentValue) => totalValue + currentValue, 0);
                const avgEncodeTime = totalEncodeTime / trackStats.length;
                const { qualityLimitationReason = QualityLimitationReason.NONE } = trackStats
                    .find((stat) => stat.qualityLimitationReason !== QualityLimitationReason.NONE) ?? {};
                const encodeResolution = trackStats
                    .map((stat) => stat.encodeResolution)
                    .reduce((resolution, currentValue) => Math.max(resolution, currentValue), 0);
                const localTrack = this._conference.getLocalVideoTracks().find(t => t.rtcId === trackId);
                const exisitingStats = this._encodeTimeStats.get(trackId);
                const sourceStats = {
                    avgEncodeTime,
                    codec,
                    encodeResolution,
                    qualityLimitationReason,
                    localTrack,
                    timestamp,
                    tpc
                };
                if (exisitingStats) {
                    exisitingStats.add(sourceStats);
                }
                else {
                    // Save stats for only the last 5 mins.
                    const data = new FixedSizeArray(300);
                    data.add(sourceStats);
                    this._encodeTimeStats.set(trackId, data);
                }
                logger.debug(`Encode stats for ${localTrack}: codec=${codec}, time=${avgEncodeTime},`
                    + `resolution=${encodeResolution}, qualityLimitationReason=${qualityLimitationReason}`);
                this._performQualityOptimizations(sourceStats);
            }
        };
    }
};
export default QualityContoller;
